import { motion } from "framer-motion";
import { ExternalLink, Star, Instagram, MapPin, ConciergeBell, Receipt, MessageSquareHeart, Megaphone, Phone, Info } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/TopBar";
import { PoweredByFudiTap } from "@/components/Logo";
import { LoyaltyCard } from "@/components/LoyaltyCard";
import { useLang } from "@/context/LanguageContext";
import { useUI } from "@/context/UIContext";
import { useOrder } from "@/context/OrderContext";
import { restaurantConfig } from "@/config/restaurantConfig";
import { localize } from "@/lib/format";
import { isOnline } from "@/lib/network";

// COMPANION_ONLY home: restaurant keeps its own external menu; FudiTap provides
// "Menüyü Aç" + service actions. A first-class commercial mode (not a fallback).
export default function CompanionHome() {
  const { lang, t } = useLang();
  const { openWaiter, openFeedback } = useUI();
  const { addAction } = useOrder();
  const { links } = restaurantConfig;
  const openUrl = (url) => url && window.open(url, "_blank", "noopener");

  const campaignsText = (restaurantConfig.promotions || []).map((p) => localize(p.title, lang)).join(" · ") || t("campaignsNone");

  const requestBill = () => {
    if (!isOnline()) {
      toast.error(t("offlineActionBlocked"));
      return;
    }
    addAction({ type: "bill" });
    toast.success(t("billRequested"), { description: t("billRequestedDesc"), icon: <Receipt className="h-4 w-4" /> });
  };

  const tiles = [
    { key: "callWaiter", icon: ConciergeBell, accent: true, onClick: openWaiter },
    { key: "requestBill", icon: Receipt, accent: true, onClick: requestBill },
    { key: "svc_feedback", icon: MessageSquareHeart, onClick: openFeedback },
    { key: "svc_google", icon: Star, onClick: () => openUrl(links.googleReviewUrl) },
    { key: "svc_instagram", icon: Instagram, onClick: () => openUrl(links.instagramUrl) },
    { key: "svc_location", icon: MapPin, onClick: () => openUrl(links.locationUrl) },
    { key: "svc_campaigns", icon: Megaphone, onClick: () => toast(t("svc_campaigns"), { description: campaignsText }) },
    { key: "svc_contact", icon: Phone, onClick: () => openUrl(`tel:${links.phone?.replace(/\s/g, "")}`) },
    { key: "svc_info", icon: Info, onClick: () => toast(restaurantConfig.name, { description: `${restaurantConfig.branch.name} · ${restaurantConfig.hours}` }) },
  ];

  return (
    <div data-testid="companion-home">
      <TopBar />
      <div className="px-4 py-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <h1 className="font-heading text-2xl font-extrabold">{t("companionTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("companionDesc")}</p>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => openUrl(links.externalMenuUrl)}
          className="mb-6 flex w-full items-center justify-between rounded-3xl brand-gradient px-6 py-5 text-white shadow-float"
          data-testid="open-external-menu-btn"
        >
          <span className="font-heading text-lg font-extrabold">{t("openMenu")}</span>
          <ExternalLink className="h-6 w-6" />
        </motion.button>

        <h2 className="mb-3 font-heading text-lg font-extrabold">{t("services")}</h2>
        <div className="grid grid-cols-3 gap-3">
          {tiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <button
                key={tile.key}
                onClick={tile.onClick}
                className={`flex flex-col items-center gap-2 rounded-2xl border p-3 text-center shadow-soft transition-colors active:scale-95 ${tile.accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
                data-testid={`companion-service-${tile.key}`}
              >
                <span className={`grid h-11 w-11 place-items-center rounded-xl ${tile.accent ? "brand-gradient text-white" : "bg-secondary text-primary"}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-[11px] font-semibold leading-tight">{t(tile.key)}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          <LoyaltyCard />
        </div>

        <PoweredByFudiTap />
      </div>
    </div>
  );
}
